import React, { useState, useEffect } from "react";
import { ReactComponent as LocationIcon } from "~/images/inline_svg/room.svg";
import ThumbBlank from "~/images/thumbBlank.jpg";
import dayjs from "dayjs";
import { Link } from "gatsby";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";

type Article = {
  node: GatsbyTypes.ContentfulEvent;
  href: string;
};

type Props = {
  articles: Article[];
};

const EVENT_STATUS = [
  {
    label: `受付中`,
    labelBgColor: `rgba(255, 100, 0, 0.8)`,
    textColor: colors.white,
    bgColor: colors.lightBlue,
  },
  {
    label: `受付終了`,
    labelBgColor: `rgba(255, 100, 0, 0.8)`,
    textColor: colors.white,
    bgColor: colors.littleBrightGray,
  },
  {
    label: `開催終了`,
    labelBgColor: `rgba(50, 50, 50, 0.8)`,
    textColor: colors.defaultText,
    bgColor: colors.white,
  },
  {
    label: `アーカイブ配信中`,
    labelBgColor: `rgba(50, 50, 50, 0.8)`,
    textColor: colors.white,
    bgColor: `rgba(119, 156, 247, 1.0)`,
  },
];

const EventArticleCard: React.FC<Article> = ({ node, href }) => {
  const { ogpImage, title, date, location, receptionStatus, endedAt, hasArchive } = node;
  const [eventStatus, setEventStatus] = useState(EVENT_STATUS[2]);

  useEffect(() => {
    const now = dayjs();
    const eventDate = dayjs(endedAt);
    const isEventEnded = now.isAfter(eventDate);

    if (isEventEnded) {
      if (hasArchive) {
        setEventStatus(EVENT_STATUS[3]); // アーカイブ配信中
      } else {
        setEventStatus(EVENT_STATUS[2]); // 開催終了
      }
    } else {
      if (hasArchive) {
        setEventStatus(EVENT_STATUS[3]); // アーカイブ配信中
      } else if (receptionStatus === true) {
        setEventStatus(EVENT_STATUS[0]); // 受付中
      } else {
        setEventStatus(EVENT_STATUS[1]); // 受付終了
      }
    }
  }, [date, hasArchive, receptionStatus, endedAt]);

  return (
    <>
      <style jsx>{`
        .articleItem {
          width: calc(100% / 3 - 30px);
          margin: 15px;
          background-color: ${colors.white};
          border-radius: 7px;
          transition: box-shadow 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        .articleItem:hover {
          box-shadow: 0 0 15px rgba(0, 0, 0, 0.25);
        }
        .articleItem:hover .thumbnail {
          opacity: 0.8;
        }
        .thumbnail {
          position: relative;
          width: 100%;
          height: 0;
          border-radius: 7px 7px 0 0;
          background-size: 100%;
          background-repeat: no-repeat;
          background-position: center;
          transition-property: background-size, opacity;
          transition-duration: 0.4s;
          transition-timing-function: ease;
          background-image: url(${ogpImage?.fluid?.src || ThumbBlank});
          padding-top: ${(ogpImage?.fluid && (1 / ogpImage.fluid.aspectRatio) * 100) || (294 / 513) * 100}%;
        }
        .thumbnail:after {
          border-radius: 7px 7px 0 0;
          content: "";
          width: 100%;
          height: 100%;
          position: absolute;
          background-color: ${eventStatus.label === `開催終了` ? `rgba(50,50,50,0.3)` : `transparent`};
          top: 0;
          left: 0;
          z-index: 1;
        }
        .dateBox {
          position: absolute;
          top: 0;
          left: 0;
          border-radius: 5px;
          padding: 10px;
          background-color: ${eventStatus.labelBgColor};
          color: ${colors.white};
          z-index: 2;
          height: 90px;
          width: 90px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          letter-spacing: 0.08rem;
        }
        .dateBox .year {
          display: block;
          text-align: center;
          font-size: 12px;
          line-height: 1;
        }
        .monthDate {
          position: relative;
          line-height: 1;
          width: 100%;
          text-align: center;
          margin-top: 5px;
        }
        .monthDate .month {
          font-size: 18px;
          vertical-align: top;
        }
        .monthDate .date {
          font-size: 35px;
        }
        .monthDate .slash {
          vertical-align: top;
          position: relative;
          top: 2px;
          font-size: 22px;
        }
        .dateBox .day {
          font-size: 18px;
          line-height: 1;
          display: block;
        }
        .accepting {
          background-color: ${eventStatus.bgColor};
          color: ${eventStatus.textColor};
          display: inline-block;
          position: absolute;
          right: 10px;
          top: 10px;
          font-size: 12px;
          line-height: 1;
          padding: 5px 10px;
          border-radius: 50px;
          z-index: 2;
        }
        .title {
          padding: 20px 30px 10px;
          color: ${colors.defaultText};
          font-size: 16px;
          font-weight: 500;
          line-height: 26px;
          letter-spacing: 1px;
        }
        .location {
          padding: 0 30px 20px;
        }
        .location .icon {
          position: relative;
          width: 20px;
          height: calc(13px * 1.8);
          margin-right: 5px;
          display: inline-block;
          vertical-align: top;
        }
        .location span {
          position: relative;
          display: inline-block;
          font-size: 13px;
          color: ${colors.littleBrightGray};
          width: calc(100% - 35px);
          height: calc(13px * 1.8);
          overflow: hidden;
        }
        .location span:before,
        .location span:after {
          position: absolute;
          background: #fff;
        }
        .location span:before {
          content: "...";
          top: 0;
          right: 0;
          display: inline-block;
          padding-left: 5px;
          color: ${colors.littleBrightGray};
        }
        .location span:after {
          content: "";
          width: 100%;
          height: 100%;
        }
        @media (${breakPoints.sp}) {
          .articleItem {
            width: 100%;
            margin: 15px 0;
          }
        }
      `}</style>
      <li className="articleItem">
        <Link to={href}>
          <div className="thumbnail">
            <div className="dateBox">
              <span className="year">{dayjs(date).format("YYYY")}</span>
              <div className="monthDate">
                <span className="month">{dayjs(date).format("MM")}</span>
                <span className="slash">&#047;</span>
                <span className="date">{dayjs(date).format("DD")}</span>
              </div>
              <span className="day">{dayjs(date).format("ddd")}</span>
            </div>
            <div className="accepting">{eventStatus.label}</div>
          </div>
          <h3 className="title">{title}</h3>
          <div className="bottomBox">
            {location && (
              <div className="location">
                <div className="icon">
                  <LocationIcon
                    style={{
                      fill: colors.brand,
                      width: "100%",
                    }}
                  />
                </div>
                <span>{location}</span>
              </div>
            )}
          </div>
        </Link>
      </li>
    </>
  );
};

export const EventArticleCards: React.FC<Props> = ({ articles }) => {
  return (
    <>
      <style jsx>{`
        ul {
          position: relative;
          width: 100%;
          display: flex;
          flex-wrap: wrap;
        }
      `}</style>
      <ul>
        {articles.map((article) => {
          return (
            <EventArticleCard
              key={`EventArticleCard_${article.node.contentful_id}`}
              node={article.node}
              href={article.href}
            />
          );
        })}
      </ul>
    </>
  );
};
